import {
    Box,
    Card,
    CardContent,
    CardHeader, Checkbox, CircularProgress,
    Divider, FormControl, FormControlLabel, FormLabel,
    Grid, InputAdornment, Link,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { m } from "framer-motion";
import { RHFRadioGroup, RHFSelect, RHFTextField, RHFCheckbox, RHFPhoneInput } from "../../../components/minimal/hook-form";
import RHFDatePicker from "../../../components/minimal/hook-form/RHFDatePicker";
import { CouponTypes, FieldsTypes } from "../../../consts";
import { LoadingButton } from "@mui/lab";
import { useLocales } from "../../../locales";
import useResponsive from "../../../hooks/minimal/useResponsive";
import { useState, useEffect } from "react";
import { getCouponByCode } from "../../../api";
import { useEventContext } from "../../../context-state/EventContext";
import { useSnackbar } from "../../../components/minimal/snackbar";
import { useFieldArray, useFormContext } from "react-hook-form";
import { isValidBirthDate, isValidEmail, isValidPhone, isValidSocialLink } from "../../../utils/validators";
import { getSeatLabel } from "../../../utils/eventPage";
import { fCurrency } from "../../../utils/minimal/formatNumber.js";
import { useTermsFormsForAccount } from "../../../hooks/useTermsForms.jsx";
import * as API from "../../../api/index.js";
import { getRelevantHrcError } from "../../../utils/transformers.js";
import { useSearchParams } from "react-router-dom";
import { getCountryCodeFromBrowserTimeZone } from "../../../utils/tzToCountry";
import { useSettingsContext } from "../../../components/minimal/settings/SettingsContext";
import { matchIsValidTel } from 'mui-tel-input'
import parsePhoneNumber from 'libphonenumber-js';
import SeasonTicketStatus from "./SeasonTicketStatus";
import checkoutStore from "../../../stores/checkout.store";
import { observer } from "mobx-react-lite";

export const DetailsForm = observer(({ totalAmountOfTickets, summary, totalToPayAfterCoupon, onValidFormSubmit, formFields, isLoading, nimiFee, isLoadingPrice, priceError, pricePerTicket }) => {
    const { translate } = useLocales()
    const settings = useSettingsContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [couponIsFocused, setCouponIsFocused] = useState(false)
    const [subscriptionIsFocused, setSubscriptionIsFocused] = useState(false)
    const isDesktop = useResponsive('up', 'md');
    const { eventPageContext } = useEventContext()
    const { account, event, coupon, setCoupon, setSeasonTicket, seasonTicket, setAppliedCouponQs, setAppliedHrcQs } = eventPageContext
    const { enqueueSnackbar } = useSnackbar();
    const { setError, handleSubmit, control, setValue, formState: { errors } } = useFormContext();
    const { fields } = useFieldArray({ control, name: "items" });
    const [isAllowTerms, setIsAllowTerms] = useState(false)
    const [mustAllowTermsError, setMustAllowTermsError] = useState('')

    /* Coupon Logic */
    const [couponCode, setCouponCode] = useState('')
    const [isCouponLoading, setIsCouponLoading] = useState(false)
    const [couponHelperText, setCouponHelperText] = useState('')

    /* HrcCode Logic */
    const [isHrcLoading, setIsHrcLoading] = useState(false)
    const [hrcCode, setHrcCode] = useState(searchParams.get('hrc') || localStorage.getItem('hrcCode') || '')
    const [hrcCodeHelperText, setHrcCodeHelperText] = useState('')
    const [hideSubscriptionDiscount, setHideSubscriptionDiscount] = useState(!!searchParams.get('hideSubscriptionDiscount'))

    const [hideSplitPayment, setHideSplitPayment] = useState(null)
    const [hideNormalPayment, setHideNormalPayment] = useState(null)

    const onHrcCodeChange = (event) => {
        setHrcCodeHelperText('')
        setHrcCode(event.target.value)
    }

    const applyHrcCode = async (qsHrcCode) => {
        try {
            const hrc = hrcCode || qsHrcCode
            if (!hrc) {
                return
            }
            setIsHrcLoading(true)
            const { data } = await API.getEndUserSeasonTicketByHrc({ sellerId: account._id, marketplaceId: account.marketPlace._id, eventId: event._id, hrc })
            if (data.isValid === false) {
                // hrc code is not valid
                throw new Error(data.message)
            } else {
                setSeasonTicket({ ...data, hrc: hrcCode })
                enqueueSnackbar('מנוי מומש בהצלחה!')
            }
        } catch (e) {
            console.log(e)
            const errorMsg = getRelevantHrcError(e)
            setHrcCodeHelperText(errorMsg)
        } finally {
            setIsHrcLoading(false)
            if (qsHrcCode) {
                setAppliedHrcQs(true)
            }
        }
    }

    const onCouponCodeChange = (event) => {
        setCouponHelperText('')
        setCouponCode(event.target.value)
    }

    const applyCouponCode = async (qsCouponCode) => {
        const code = couponCode || qsCouponCode
        if (!code) {
            return
        }
        setIsCouponLoading(true)
        try {
            const couponResponse = await getCouponByCode({ accountId: account._id, eventId: event._id, code, })
            if (couponResponse?.data?._id) {
                setCoupon(couponResponse.data)
                enqueueSnackbar('קופון הופעל בהצלחה!')
            } else {
                setCouponHelperText('קופון לא נמצא או לא ניתן לשימוש יותר')
                setCoupon(null)
            }
        } catch (e) {
            console.log(e)
        } finally {
            setIsCouponLoading(false)
            if (qsCouponCode) {
                setAppliedCouponQs(true)
            }
        }

    }

    const onTermsCheckboxChanged = () => {
        setIsAllowTerms(!isAllowTerms)
        setMustAllowTermsError("")
    }

    const onFormSubmit = (data) => {

        let hasError = ""

        const alreadyExisting = { phones: {}, emails: {}, socials: {} }

        data.items.forEach((field, index) => {

            const { fname, lname, phone, email, gender, birth_date, social_link } = field
            const baseFieldName = `items[${index}]`
            const baseErrorMessage = { message: translate('dashboard.events.required_field') }

            if (!fname && formFields.fnameField.required) {
                hasError = baseErrorMessage.message
                setError(`${baseFieldName}.fname`, baseErrorMessage)
            }
            if (!lname && formFields.lnameField.required) {
                hasError = baseErrorMessage.message
                setError(`${baseFieldName}.lname`, baseErrorMessage)
            }
            if (!gender && formFields.genderField.required) {
                hasError = baseErrorMessage.message
                setError(`${baseFieldName}.gender`, baseErrorMessage)
            }
            if ((!birth_date || !isValidBirthDate(birth_date)) && formFields.birthdayField.required) {
                hasError = translate('event_page.invalid_date')
                setError(`${baseFieldName}.birth_date`, { message: translate('event_page.invalid_date') })
            }
            /* Social Link */
            if (formFields.socialLinkField.required) {
                if ((!social_link || !isValidSocialLink(social_link))) {
                    hasError = translate('event_page.invalid_social_link')
                    setError(`${baseFieldName}.social_link`, { message: translate('event_page.invalid_social_link') })
                } else {
                    if (alreadyExisting.socials[social_link]) {
                        hasError = translate('event_page.already_exists_social')
                        setError(`${baseFieldName}.social_link`, { message: translate('event_page.already_exists_social') })
                    }
                    alreadyExisting.socials[social_link] = true
                }
            }

            /* Phone */
            if (formFields.phoneField.required) {
                if (!phone || !isValidPhone(phone)) {
                    hasError = translate('event_page.invalid_phone')
                    setError(`${baseFieldName}.phone`, { message: translate('event_page.invalid_phone') })
                } else {
                    if (alreadyExisting.phones[phone]) {
                        hasError = translate('event_page.already_exists_phone')
                        setError(`${baseFieldName}.phone`, { message: translate('event_page.already_exists_phone') })
                    }
                    alreadyExisting.phones[phone] = true
                }
            }

            /* Email */
            if (formFields.emailField.required) {
                if (!email || !isValidEmail(email)) {
                    hasError = translate('event_page.invalid_email')
                    setError(`${baseFieldName}.email`, { message: translate('event_page.invalid_email') })
                } else {
                    if (alreadyExisting.emails[email]) {
                        hasError = translate('event_page.already_exists_email')
                        setError(`${baseFieldName}.email`, { message: translate('event_page.already_exists_email') })
                    }
                    alreadyExisting.emails[email] = true
                }
            }

            /* Custom Questions */
            if (formFields.custom_questions?.length) {
                for (const customQ of formFields.custom_questions) {
                    const { custom_required, custom_name } = customQ // can do validations base on the 'custom_type' property (date, string, boolean etc.)
                    if (custom_required) {
                        if (!field[custom_name]) {
                            hasError = baseErrorMessage.message
                            setError(`${baseFieldName}.${custom_name}`, baseErrorMessage)
                        }
                    }
                }
            }
        })

        if (hasError) {
            setMustAllowTermsError(hasError);
            enqueueSnackbar(translate('event_page.form_error'), { variant: 'error' });
            return
        }

        if (!isAllowTerms) {
            setMustAllowTermsError('חובה לאשר את התקנון לפני ביצוע רכישה')
            return
        }

        return true
    }

    const { privacyPolicy, termsOfService } = useTermsFormsForAccount(account)

    useEffect(() => {
        const hrcCode = searchParams.get('hrc') || localStorage.getItem('hrcCode')
        if (hrcCode) {
            setHrcCode(hrcCode)
            applyHrcCode(hrcCode)
        }
    }, [searchParams.get('hrc')])

    useEffect(() => {
        if (searchParams.get('coupon')) {
            setCouponCode(searchParams.get('coupon'))
            applyCouponCode(searchParams.get('coupon'))
        }
    }, [searchParams.get('coupon')])

    useEffect(() => {
        setHideSubscriptionDiscount(!!searchParams.get('hideSubscriptionDiscount'))
    }, [searchParams.get('hideSubscriptionDiscount')])

    const defaultCountry = getCountryCodeFromBrowserTimeZone('IL')
    const prePopulatedPhone = fields[0].phone // when entering the season ticket code, we will pre-populate the phone number

    useEffect(() => {
        try {
            if (!prePopulatedPhone) { // if there is no pre-populated phone, nothing to do
                return
            }
            if (formFields.phoneField.required == false) { // if the phone is not required, nothing to do
                return
            }
            const defaultPhoneIsValid = matchIsValidTel(prePopulatedPhone)
            if (defaultPhoneIsValid) { // nothing to do as wel, the phone is valid
                return
            }
            // if the phone is not valid, we will try to parse it and then set it to the form value
            const parsedPhone = parsePhoneNumber(prePopulatedPhone, defaultCountry);
            if (parsedPhone && parsedPhone.isValid()) {
                setValue(`items[${0}].phone`, parsedPhone.number)
            } else {
                // if the phone is not valid, we will not pre-populate it and make the user enter it manually
                setValue(`items[${0}].phone`, '')
            }
        } catch (e) {
            console.error(e)
        }
    }, [setValue, prePopulatedPhone]);

    // Updated handler functions for payment buttons
    const onNormalPayment = handleSubmit((data) => {
        checkoutStore.splitPayment = false;
        const formIsValid = onFormSubmit(data);
        if (formIsValid) {
            onValidFormSubmit(data);
            setHideSplitPayment(true)
        }
    });

    const onSplitPayment = handleSubmit(async (data) => {
        checkoutStore.splitPayment = true;
        const formIsValid = onFormSubmit(data);
        if (formIsValid) {
            onValidFormSubmit(data);
            setHideNormalPayment(true)
        }
    });

    return (
        <>
            <Grid initial={{ opacity: 0, x: 80 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} component={m.div} item xs={12} md={6}>
                {
                    fields.map((item, index) => {
                        const head = `${translate('event_page.order_details')} ${index + 1}# - ${item.ticketName}`

                        return (
                            <Card sx={{ p: 2, mb: 2 }} key={index}>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle1">{head}</Typography>
                                    <Stack spacing={1} direction={!isDesktop ? 'column' : 'row'}>
                                        {
                                            (formFields.fnameField.required || !formFields.fnameField.isHidden)
                                                ? <RHFTextField name={`items[${index}].fname`} label={formFields.fnameField.placeholder} required={formFields.fnameField.required} />
                                                : null
                                        }
                                        {
                                            (formFields.lnameField.required || !formFields.lnameField.isHidden)
                                                ? <RHFTextField name={`items[${index}].lname`} label={formFields.lnameField.placeholder} required={formFields.lnameField.required} />
                                                : null
                                        }
                                    </Stack>
                                    <Stack spacing={1} direction={!isDesktop ? 'column' : 'row'}>
                                        <RHFTextField name={`items[${index}].email`} label={formFields.emailField.placeholder} required={formFields.emailField.required} />
                                        <RHFPhoneInput
                                            forceCallingCode={true}
                                            sx={{ direction: settings?.themeDirection }}
                                            defaultCountry={defaultCountry}
                                            name={`items[${index}].phone`}
                                            type="tel"
                                            label={formFields.phoneField.placeholder}
                                            required={formFields.phoneField.required}
                                        />
                                    </Stack>
                                    <Stack spacing={1} direction={!isDesktop ? 'column' : 'row'}>
                                        {
                                            (formFields.genderField.required || !formFields.genderField.isHidden)
                                                ? (
                                                    <RHFSelect label={formFields.genderField.placeholder} name={`items[${index}].gender`} required={formFields.genderField.required}>
                                                        <MenuItem value="male">{translate('event_page.male')}</MenuItem>
                                                        <MenuItem value="female">{translate('event_page.female')}</MenuItem>
                                                        <MenuItem value="other">{translate('event_page.other')}</MenuItem>
                                                    </RHFSelect>
                                                )
                                                : null
                                        }
                                        {
                                            (formFields.birthdayField.required || !formFields.birthdayField.isHidden)
                                                ? <RHFDatePicker inputSx={{ width: "100%" }} name={`items[${index}].birth_date`} disableFuture label={formFields.birthdayField.placeholder} required={formFields.birthdayField.required} />
                                                : null
                                        }

                                    </Stack>
                                    <Stack spacing={1}>
                                        {
                                            (formFields.socialLinkField.required || !formFields.socialLinkField.isHidden)
                                                ? <RHFTextField name={`items[${index}].social_link`} label={formFields.socialLinkField.placeholder} required={formFields.socialLinkField.required} />
                                                : null
                                        }
                                    </Stack>
                                    {formFields.custom_questions?.length > 0 && <Stack>{renderCustomQuestionsFields(formFields.custom_questions, index)}</Stack>}
                                </Stack>
                            </Card>
                        )
                    })
                }
            </Grid>
            <Grid initial={{ opacity: 0, x: -80 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} component={m.div} item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                    <CardHeader title={translate('event_page.order_summary')} />
                    <CardContent>
                        <Stack spacing={2}>
                            <PriceDetails orderSummary={summary} pricePerTicket={pricePerTicket} />
                            <SeasonTicketStatus seasonTicket={seasonTicket} />
                            {
                                !coupon ? null :
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" sx={{ color: 'info.main' }}>
                                            {coupon.code}
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ color: 'info.main' }}>
                                            {coupon.type === CouponTypes.FIXED && `₪${coupon.amount * totalAmountOfTickets}-`}
                                            {coupon.type === CouponTypes.PERCENTAGE && `${coupon.amount}%-`}
                                            {coupon.type === CouponTypes.CUSTOM_PRICE && `מחיר קבוע - ₪${coupon.amount}`}
                                        </Typography>
                                    </Stack>

                            }
                            {
                                !nimiFee ? null :
                                    <Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                עמלת רכישה
                                            </Typography>
                                            <Typography variant="subtitle2">
                                                {fCurrency(nimiFee)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                            }
                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="subtitle1">{translate('event_page.total_to_pay')}</Typography>
                                <Box sx={{ textAlign: 'right' }}>
                                    {
                                        isLoadingPrice ? <CircularProgress size={22} />
                                            : <Stack direction="row" alignItems="center">
                                                {hideSubscriptionDiscount && hrcCodeHelperText && <Typography sx={{ color: "error.main", fontSize: "12px", margin: '0 8px' }}>{hrcCodeHelperText}</Typography>}
                                                <Typography variant="subtitle1" sx={{ color: priceError ? 'error.main' : 'primary.main' }}>
                                                    {priceError ? priceError : fCurrency(totalToPayAfterCoupon)}
                                                </Typography>
                                            </Stack>
                                    }

                                    {/*<Typography variant="caption" sx={{ fontStyle: 'italic' }}>*/}
                                    {/*    (VAT included if applicable)*/}
                                    {/*</Typography>*/}
                                </Box>
                            </Stack>

                            {
                                event.isAllowCoupon ?
                                    <TextField
                                        name="couponCode"
                                        fullWidth
                                        size="medium"
                                        onChange={onCouponCodeChange}
                                        placeholder="קוד קופון"
                                        onFocus={() => setCouponIsFocused(true)}
                                        onBlur={() => setCouponIsFocused(false)}
                                        value={couponCode}
                                        helperText={couponHelperText}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <LoadingButton variant={couponIsFocused ? "contained" : "outlined"} loading={isCouponLoading} onClick={applyCouponCode}>
                                                        הפעל
                                                    </LoadingButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    : null
                            }
                            {
                                event.isAllowSeasonTicket ?
                                    <TextField
                                        sx={{
                                            visibility: hideSubscriptionDiscount ? 'hidden' : 'visible',
                                            height: hideSubscriptionDiscount ? 0 : 'unset',
                                        }}
                                        name="seasonTicket"
                                        fullWidth
                                        size="medium"
                                        onChange={onHrcCodeChange}
                                        onFocus={() => setSubscriptionIsFocused(true)}
                                        onBlur={() => setSubscriptionIsFocused(false)}
                                        placeholder="קוד מנוי"
                                        value={hrcCode}
                                        helperText={hrcCodeHelperText}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <LoadingButton variant={subscriptionIsFocused ? "contained" : "outlined"} loading={isHrcLoading} onClick={applyHrcCode}>
                                                        הפעל
                                                    </LoadingButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    : null
                            }



                            <Stack spacing={2}>
                                <Stack spacing={2} direction={!isDesktop ? 'column' : 'row'}>
                                    {(event.allowSplitPayment && !hideSplitPayment) && <LoadingButton
                                        disabled={!!priceError || isLoadingPrice}
                                        loading={(isLoading && !isLoadingPrice) || checkoutStore.prepareSplitPayment}
                                        onClick={onSplitPayment} size="large"
                                        sx={{ width: "100%" }}
                                        variant="outlined">
                                        {translate('event_page.split_order')}
                                    </LoadingButton>}

                                    {!hideNormalPayment && <LoadingButton
                                        disabled={!!priceError || isLoadingPrice}
                                        loading={isLoading && !isLoadingPrice}
                                        size="large"
                                        onClick={onNormalPayment}
                                        sx={{ width: "100%" }}
                                        variant="contained">
                                        {totalToPayAfterCoupon === 0 && !isLoadingPrice ? translate('event_page.complete_order') : translate('event_page.pay')}
                                    </LoadingButton>}

                                </Stack>

                                <Stack>
                                    {/* <FormControlLabel
                                       label={
                                           <Typography sx={{ fontSize: "12px" }}>
                                               <span>הנני מאשר קבלת תוכן פרסומי באימייל  </span>
                                           </Typography>
                                       }
                                       control={<Checkbox checked={isAllowTerms} onChange={onTermsCheckboxChanged} />}
                                   /> */}
                                    <FormControlLabel
                                        label={
                                            <Typography>
                                                <span>קראתי את </span>
                                                <Link href={termsOfService} target="_blank" sx={{ mr: "3px" }}>תנאי השימוש</Link>
                                                <span>ואני מסכים</span>
                                                <span> לתנאים ולמדיניות ביטולים</span>
                                            </Typography>
                                        }
                                        control={<Checkbox checked={isAllowTerms} onChange={onTermsCheckboxChanged} />}
                                    />
                                    {formFields.emailMarketingField && <Stack spacing={0} style={{ marginTop: 0 }}>
                                        {
                                            (!formFields.emailMarketingField?.isHidden)
                                                ? <RHFCheckbox name={`items[0].isAllowMarketingDelivery`} label={<Typography>{translate(formFields.emailMarketingField.placeholder)}</Typography>} required={formFields.emailMarketingField.required} />
                                                : null
                                        }
                                    </Stack>}
                                    {mustAllowTermsError ? <Typography sx={{ color: "error.main", fontSize: "12px" }}>{mustAllowTermsError}</Typography> : null}
                                </Stack>

                                {/*<FormControlLabel*/}
                                {/*    label={<Typography sx={{ fontSize: "12px" }}>הנני מאשר הרשמה לניוזלטר וקבלת עדכונים על אירועים חדשים</Typography>}*/}
                                {/*    control={<Checkbox checked={false} onChange={() => console.log("eee")} />}*/}
                                {/*/>*/}

                            </Stack>




                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </>
    )
})


const renderCustomQuestionsFields = (customQs, mainIndex) => {
    return customQs.map(({ custom_required, custom_type, custom_placeholder, custom_name }, i) => {

        const fieldName = `items[${mainIndex}].${custom_name}`
        let field = <RHFTextField name={fieldName} label={custom_placeholder} required={custom_required} />

        if (custom_type === FieldsTypes.DATE_NO_TIME) {
            field = <RHFDatePicker name={fieldName} label={custom_placeholder} required={custom_required} />
        }

        if (custom_type === FieldsTypes.YES_OR_NO) {
            field = (
                <FormControl>
                    <FormLabel required={custom_required}>{custom_placeholder}</FormLabel>
                    <RHFRadioGroup
                        row
                        name={fieldName}
                        options={[
                            { label: 'כן', value: 1 },
                            { label: 'לא', value: 0 },
                        ]}
                    />
                </FormControl>
            )
        }

        return (
            <Stack key={i} spacing={1}>
                {field}
            </Stack>
        )
    })
}

const PriceDetails = ({ orderSummary, pricePerTicket }) => {

    const uniquePriceLevels = orderSummary.reduce((acc, row) => {
        if (!acc.includes(row.priceLevel)) {
            acc.push(row.priceLevel)
        }
        return acc
    }, [])

    return (<>
        {orderSummary.map((row, index) => {
            const ticketSeatsInfo = getSeatLabel(row)
            const priceData = pricePerTicket[index]
            const discount = priceData && priceData.price > priceData.priceAfterDiscount
            return (
                <Stack key={index} direction="row" justifyContent="space-between">
                    <Stack>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {index + 1}# {row.ticketName}
                        </Typography>
                        {ticketSeatsInfo ? ticketSeatsInfo : null}
                    </Stack>
                    <Stack justifyContent={"flex-end"} sx={{ textAlign: "right" }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{uniquePriceLevels.length > 1 ? " מחיר " + row.priceLevel : ""}</Typography>
                        {discount && <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}><s>₪{priceData.price}</s></Typography>}
                        {priceData && <Typography variant="subtitle2" > ₪{priceData.priceAfterDiscount.toFixed(2)}</Typography>}
                        {!priceData && <Typography variant="subtitle2" sx={{ color: 'text.secondary' }} >₪{row.totalPrice}</Typography>}
                    </Stack>
                </Stack >
            )
        })}
    </>)
}